import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Save, Trash2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { input, mxn, GrHeader } from "./_grShared";

const CATS = [
  { key: "acabados", label: "Nivel de acabados", options: [{ label: "Estándar", price: 0 }, { label: "Plus", price: 80000 }, { label: "Premium", price: 180000 }] },
  { key: "cocina", label: "Cocina equipada", options: [{ label: "Básica", price: 0 }, { label: "Integral", price: 65000 }, { label: "Chef", price: 140000 }] },
  { key: "closets", label: "Closets y carpintería", options: [{ label: "Estándar", price: 0 }, { label: "Diseño", price: 45000 }, { label: "Walk-in", price: 95000 }] },
  { key: "banos", label: "Baños", options: [{ label: "Estándar", price: 0 }, { label: "Spa", price: 55000 }] },
  { key: "domotica", label: "Domótica", options: [{ label: "Sin domótica", price: 0 }, { label: "Básica", price: 30000 }, { label: "Full smart", price: 90000 }] },
  { key: "terraza", label: "Terraza / Exterior", options: [{ label: "Sin terraza", price: 0 }, { label: "Deck", price: 40000 }, { label: "Roof garden", price: 120000 }] },
];

export default function GrConfigurator() {
  const [base, setBase] = useState(3000000);
  const [sel, setSel] = useState({});
  const [client, setClient] = useState("");
  const [property, setProperty] = useState("");
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/gr/config").then((r) => setSaved(r.data)).catch(() => {}).finally(() => setLoading(false));
  const [sp] = useSearchParams();
  useEffect(() => {
    load();
    const b = sp.get("base"); const prop = sp.get("property"); const cl = sp.get("client");
    if (b && Number(b) > 0) setBase(Number(b));
    if (prop) setProperty(prop);
    if (cl) setClient(cl);
    /* eslint-disable-next-line */
  }, []);

  const extras = useMemo(() => CATS.reduce((s, c) => s + (sel[c.key]?.price || 0), 0), [sel]);
  const total = Number(base || 0) + extras;

  const save = async () => {
    if (!client.trim()) return toast.error("Cliente requerido");
    const selections = CATS.map((c) => ({ category: c.label, option: sel[c.key]?.label || c.options[0].label, price: sel[c.key]?.price || 0 }));
    try { await api.post("/gr/config", { client, property, base_price: Number(base), selections, extras, total, version: `v${saved.length + 1}` }); toast.success("Configuración guardada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <OSLayout title="OMNIA Interior Configurator">
      <div className="space-y-6" data-testid="gr-config-page">
        <GrHeader title="OMNIA Interior Configurator" desc="Diseña el interior de la vivienda con presupuesto en vivo: cada elección actualiza el costo total." />

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 card-surface p-5 space-y-4">
            <div><label className="text-xs text-navy/50 mb-1 block">Precio base de la vivienda</label><input type="number" className={input} value={base} onChange={(e) => setBase(e.target.value)} data-testid="gr-cfg-base" /></div>
            {CATS.map((c) => (
              <div key={c.key}>
                <label className="text-xs text-navy/50 mb-1 block flex items-center gap-1.5"><SlidersHorizontal className="w-3 h-3" /> {c.label}</label>
                <div className="flex flex-wrap gap-2" data-testid={`gr-cfg-${c.key}`}>
                  {c.options.map((o) => {
                    const on = (sel[c.key]?.label || c.options[0].label) === o.label;
                    return <button key={o.label} onClick={() => setSel({ ...sel, [c.key]: o })} className={`px-3 py-2 rounded-lg border text-sm transition-colors ${on ? "border-violet bg-violet/10 text-violet" : "border-navy/10 hover:border-violet/40"}`} data-testid={`gr-cfg-opt-${c.key}-${o.label}`}>{o.label} <span className="text-xs opacity-60">+{mxn(o.price)}</span></button>;
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="card-surface p-5 h-fit sticky top-4">
            <div className="overline text-navy/40 mb-1">Presupuesto</div>
            <div className="text-sm text-navy/50 flex justify-between py-1"><span>Base</span><span>{mxn(base)}</span></div>
            <div className="text-sm text-navy/50 flex justify-between py-1 border-b border-navy/10 mb-2"><span>Personalizaciones</span><span>+{mxn(extras)}</span></div>
            <div className="font-editorial text-3xl text-violet mb-4" data-testid="gr-cfg-total">{mxn(total)}</div>
            <div className="space-y-2">
              <input className={input} placeholder="Cliente" value={client} onChange={(e) => setClient(e.target.value)} data-testid="gr-cfg-client" />
              <input className={input} placeholder="Propiedad / Unidad" value={property} onChange={(e) => setProperty(e.target.value)} />
              <button onClick={save} className="btn btn-primary w-full" data-testid="gr-cfg-save"><Save className="w-4 h-4" /> Guardar versión</button>
            </div>
          </div>
        </div>

        {!loading && saved.length > 0 && (
          <div className="card-surface p-4" data-testid="gr-cfg-saved">
            <div className="overline text-navy/40 mb-3">Versiones guardadas</div>
            <div className="space-y-1.5">{saved.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm border-b border-navy/5 py-1.5">
                <span><b>{s.client}</b>{s.property ? ` · ${s.property}` : ""} <span className="badge badge-violet ml-1">{s.version}</span></span>
                <span className="flex items-center gap-3"><span className="text-violet font-medium">{mxn(s.total)}</span><button onClick={async () => { await api.delete(`/gr/config/${s.id}`); load(); }} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button></span>
              </div>
            ))}</div>
          </div>
        )}
      </div>
    </OSLayout>
  );
}
